import React, { useEffect, useState } from 'react';
import { BookA, Loader2, Plus, Save, Trash2 } from 'lucide-react';

import { callingBehaviorConfigAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { toast } from '../ui/use-toast';

// 2026-10-04 (Docs/AI_CALL_CENTER_PRODUCTION_READINESS_PLAN.md 4.3): words the
// voice says wrong (pronunciations) and words speech-to-text hears wrong
// (corrections). Stored in the account's calling settings (pronunciations /
// stt_corrections); the backend applies them as whole-word, case-insensitive
// replacements (app/services/calling/speech_text_rules.py).

type Row = { from: string; to: string };

const toRows = (map: any): Row[] =>
  Object.entries(map && typeof map === 'object' ? map : {}).map(([from, to]) => ({ from, to: String(to) }));

const toMap = (rows: Row[]) =>
  Object.fromEntries(rows.map((r) => [r.from.trim(), r.to.trim()]).filter(([f, t]) => f && t));

function errorText(err: any): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
  return err?.message || 'Failed to save';
}

function RowsEditor({
  title, help, fromLabel, toLabel, rows, onChange,
}: {
  title: string; help: string; fromLabel: string; toLabel: string; rows: Row[]; onChange: (rows: Row[]) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{title}</p>
          <p className="text-xs text-muted-foreground">{help}</p>
        </div>
        <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => onChange([...rows, { from: '', to: '' }])}>
          <Plus className="h-3.5 w-3.5" /> Add
        </Button>
      </div>
      {rows.length === 0 && <p className="text-xs text-muted-foreground">None yet.</p>}
      {rows.map((row, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            className="h-8"
            value={row.from}
            placeholder={fromLabel}
            onChange={(e) => onChange(rows.map((r, i) => (i === index ? { ...r, from: e.target.value } : r)))}
          />
          <span className="text-muted-foreground">→</span>
          <Input
            className="h-8"
            value={row.to}
            placeholder={toLabel}
            onChange={(e) => onChange(rows.map((r, i) => (i === index ? { ...r, to: e.target.value } : r)))}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 w-8 shrink-0 p-0 text-muted-foreground hover:text-destructive"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
            aria-label="Remove"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
    </div>
  );
}

export default function CallingVocabularySettings() {
  const [pronunciations, setPronunciations] = useState<Row[] | null>(null);
  const [corrections, setCorrections] = useState<Row[]>([]);
  const [saving, setSaving] = useState(false);

  const apply = (data: any) => {
    const e = data?.effective_settings || {};
    setPronunciations(toRows(e.pronunciations));
    setCorrections(toRows(e.stt_corrections));
  };

  useEffect(() => {
    callingBehaviorConfigAPI.getConfig().then(apply).catch(() => setPronunciations([]));
  }, []);

  if (pronunciations === null) {
    return (
      <div className="flex items-center justify-center py-6 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await callingBehaviorConfigAPI.updateConfig({
        settings: { pronunciations: toMap(pronunciations), stt_corrections: toMap(corrections) },
      });
      apply(updated);
      toast({ title: 'Saved', description: 'Words & names updated.' });
    } catch (err: any) {
      toast({ title: 'Save failed', description: errorText(err), variant: 'destructive' as any });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BookA className="h-4 w-4" /> Words &amp; names
        </CardTitle>
        <CardDescription>
          Fix how the AI says or hears facility names, medicines and local words. Whole words, upper/lower case
          doesn't matter.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <RowsEditor
          title="How to say it"
          help="The voice reads the left side as the right side (calls and voice notes). E.g. PrEP → prep."
          fromLabel="Written as"
          toLabel="Say it like"
          rows={pronunciations}
          onChange={setPronunciations}
        />
        <div className="border-t border-border/60 pt-4">
          <RowsEditor
            title="Commonly misheard on calls"
            help="Fixes what speech-to-text hears before the AI reads it. E.g. pre exposure → PrEP."
            fromLabel="Heard as"
            toLabel="Should be"
            rows={corrections}
            onChange={setCorrections}
          />
        </div>
        <div className="flex justify-end border-t border-border/60 pt-4">
          <Button onClick={handleSave} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
